import React, { useState, useEffect } from 'react';
import { createAndSendVerificationCode, verifyCode } from '../services/emailVerificationService';

/**
 * Masque partiellement un email pour la sécurité
 * Exemple: user@example.com -> u***@e***.com
 */
const maskEmail = (email) => {
  if (!email || !email.includes('@')) {
    return email;
  }
  
  const [localPart, domain] = email.split('@');
  const [domainName, ...domainExt] = domain.split('.');
  
  // Masquer le début du local part (garder 1 caractère)
  const maskedLocal = localPart.length > 1 
    ? `${localPart[0]}${'*'.repeat(Math.min(3, localPart.length - 1))}`
    : localPart;
  
  // Masquer le début du domaine (garder 1 caractère)
  const maskedDomain = domainName.length > 1
    ? `${domainName[0]}${'*'.repeat(Math.min(3, domainName.length - 1))}`
    : domainName;
  
  return `${maskedLocal}@${maskedDomain}.${domainExt.join('.')}`;
};

function EmailVerificationModal({ email, isOpen, onClose, onVerified }) {
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [error, setError] = useState('');
  const [lastSentTime, setLastSentTime] = useState(0);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  
  // Rate limiting : 60 secondes entre chaque envoi
  const EMAIL_SEND_COOLDOWN = 60000; // 60 secondes

  // Envoyer le code automatiquement à l'ouverture de la modal
  useEffect(() => {
    if (isOpen && email) {
      sendVerificationCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fonction pour envoyer le code de vérification
  const sendVerificationCode = async () => {
    if (!email) return;

    // Rate limiting : vérifier le délai depuis le dernier envoi
    const now = Date.now();
    const timeSinceLastSend = now - lastSentTime;
    
    if (timeSinceLastSend < EMAIL_SEND_COOLDOWN && lastSentTime > 0) {
      const remainingSeconds = Math.ceil((EMAIL_SEND_COOLDOWN - timeSinceLastSend) / 1000);
      setError(`Veuillez patienter ${remainingSeconds} seconde${remainingSeconds > 1 ? 's' : ''} avant de renvoyer le code.`);
      return;
    }

    try {
      setIsSendingCode(true);
      setError('');
      
      await createAndSendVerificationCode(email);
      
      // Enregistrer le temps d'envoi
      setLastSentTime(now);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Code de vérification envoyé avec succès');
      }
      setVerificationCode(['', '', '', '', '', '']);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur lors de l\'envoi du code:', error);
      }
      setError(error.message || 'Impossible d\'envoyer le code de vérification. Veuillez réessayer.');
    } finally {
      setIsSendingCode(false);
    }
  };
  
  // Compte à rebours pour le rate limiting
  useEffect(() => {
    if (lastSentTime > 0) {
      const interval = setInterval(() => {
        const now = Date.now();
        const timeSinceLastSend = now - lastSentTime;
        const remainingSeconds = Math.ceil((EMAIL_SEND_COOLDOWN - timeSinceLastSend) / 1000);
        
        if (remainingSeconds > 0) {
          setCooldownSeconds(remainingSeconds);
        } else {
          setCooldownSeconds(0);
          clearInterval(interval);
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [lastSentTime]);

  // Fonction pour vérifier le code
  const handleVerifyCode = async () => {
    const codeString = verificationCode.join('');
    if (codeString.length !== 6) {
      setError('Veuillez saisir le code complet');
      return false;
    }

    try {
      setIsVerifyingCode(true);
      setError('');
      
      const isValid = await verifyCode(email, codeString);
      
      if (isValid) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Code vérifié avec succès');
        }
        // Attendre un peu pour que le loader soit visible
        await new Promise(resolve => setTimeout(resolve, 800));
        onVerified();
        return true;
      } else {
        setError('Code de vérification invalide ou expiré. Veuillez réessayer.');
        setVerificationCode(['', '', '', '', '', '']);
        return false;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur lors de la vérification du code:', error);
      }
      setError(error.message || 'Erreur lors de la vérification. Veuillez réessayer.');
      return false;
    } finally {
      setIsVerifyingCode(false);
    }
  };

  // Gestion du code de vérification
  const handleVerificationCodeChange = (index, value) => {
    // Ne permettre que les chiffres
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    setError('');

    // Passer automatiquement au champ suivant si un chiffre est saisi
    if (value && index < 5) {
      const nextInput = document.getElementById(`verification-code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleVerificationCodeKeyDown = (index, e) => {
    // Permettre de supprimer et passer au champ précédent
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`verification-code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handlePasteCode = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const codeArray = pastedData.split('');
      setVerificationCode(codeArray);
      // Focus sur le dernier champ
      const lastInput = document.getElementById('verification-code-5');
      if (lastInput) lastInput.focus();
    }
  };

  // Vérifier automatiquement le code quand les 6 chiffres sont remplis
  useEffect(() => {
    const codeString = verificationCode.join('');
    if (codeString.length === 6 && !isVerifyingCode) {
      // Attendre un peu pour que l'utilisateur finisse de taper
      const timer = setTimeout(() => {
        handleVerifyCode();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode]);

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-container {
          background: linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%);
          border-radius: 24px;
          padding: 40px;
          width: 100%;
          max-width: 480px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.1);
          position: relative;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }

        .modal-title {
          font-size: 28px;
          font-weight: 700;
          color: #ffffff;
          font-family: 'Outfit', sans-serif;
          margin: 0;
        }

        .modal-close {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          color: #ffffff;
        }

        .modal-close:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .modal-subtitle {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.7);
          font-family: 'Inter', sans-serif;
          line-height: 1.6;
          margin-bottom: 30px;
          text-align: center;
        }

        .modal-subtitle strong {
          color: #ffffff;
          font-weight: 600;
        }

        .verification-code-container {
          display: flex;
          justify-content: center;
          gap: 12px;
          margin-bottom: 24px;
        }

        .verification-code-input {
          width: 50px;
          height: 60px;
          text-align: center;
          font-size: 24px;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.05);
          color: #ffffff;
          transition: all 0.3s ease;
          outline: none;
        }

        .verification-code-input:focus {
          border-color: #d6ab78;
          background: rgba(255, 255, 255, 0.08);
          box-shadow: 0 0 0 3px rgba(214, 171, 120, 0.2);
          transform: translateY(-2px);
        }

        .verification-code-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .modal-error {
          background: rgba(255, 76, 76, 0.2);
          border: 1px solid rgba(255, 76, 76, 0.5);
          color: #ff6b6b;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 20px;
          font-family: 'Inter', sans-serif;
          text-align: center;
        }

        .modal-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: center;
        }

        .modal-link {
          background: none;
          border: none;
          color: #d6ab78;
          font-size: 14px;
          font-family: 'Inter', sans-serif;
          cursor: pointer;
          text-decoration: underline;
          transition: color 0.3s ease;
          padding: 8px;
        }

        .modal-link:hover:not(:disabled) {
          color: #c49a67;
        }

        .modal-link:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .modal-loader-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(4px);
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }

        .modal-loader {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
        }

        .modal-loader-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid rgba(214, 171, 120, 0.2);
          border-top-color: #d6ab78;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .modal-loader-text {
          color: #ffffff;
          font-size: 16px;
          font-family: 'Inter', sans-serif;
          font-weight: 500;
        }
      `}</style>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">Vérifiez votre e-mail</h3>
            <button className="modal-close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          
          <p className="modal-subtitle">
            Veuillez saisir le code de vérification à 6 chiffres envoyé à <strong>{maskEmail(email)}</strong>
          </p>
          
          {error && (
            <div className="modal-error">{error}</div>
          )}
          
          <div className="verification-code-container" onPaste={handlePasteCode}>
            {verificationCode.map((digit, index) => (
              <input
                key={index}
                id={`verification-code-${index}`}
                type="text"
                inputMode="numeric"
                maxLength="1"
                className="verification-code-input"
                value={digit}
                onChange={(e) => handleVerificationCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleVerificationCodeKeyDown(index, e)}
                disabled={isVerifyingCode}
                autoFocus={index === 0}
              />
            ))}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="modal-link"
              onClick={sendVerificationCode}
              disabled={isSendingCode || isVerifyingCode}
            >
              {isSendingCode 
                ? 'Envoi en cours...' 
                : cooldownSeconds > 0 
                  ? `Renvoyer le code (${cooldownSeconds}s)`
                  : `Renvoyer le code à ${maskEmail(email)}`}
            </button>
          </div>

          {isVerifyingCode && (
            <div className="modal-loader-overlay">
              <div className="modal-loader">
                <div className="modal-loader-spinner"></div>
                <div className="modal-loader-text">Vérification en cours...</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default EmailVerificationModal;
